import { Link } from 'react-router-dom';
import { 
  Zap, 
  Shield, 
  Users, 
  Clock, 
  CheckCircle2,
  ChevronRight,
  Battery,
  Gauge,
  Star,
  Car,
  Send,
  Loader2
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    is_vtc_driver: false,
    platform: '',
    rental_duration: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, is_vtc_driver: checked }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setIsSubmitted(true);
      toast.success('Demande envoyée avec succès !');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "100% Électrique",
      description: "Réduisez vos coûts d'exploitation avec un véhicule électrique premium."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Idéal Uber & Bolt",
      description: "Véhicule parfaitement adapté aux plateformes VTC."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Fiable & Sécurisé",
      description: "Véhicules premium réputés pour leur fiabilité."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Validation Rapide",
      description: "Processus d'approbation simplifié pour démarrer rapidement."
    }
  ];

  const vehicles = [
    {
      name: "Tesla Model Y",
      type: "SUV 100% Électrique",
      image: "https://images.unsplash.com/photo-1669625397388-32934837bd3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxUZXNsYSUyME1vZGVsJTIwWSUyMGV4dGVyaW9yJTIwYmxhY2t8ZW58MHx8fHwxNzczMDQxMTYzfDA&ixlib=rb-4.1.0&q=85",
      features: [
        { icon: <Battery className="w-5 h-5" />, label: "100% Électrique" },
        { icon: <Gauge className="w-5 h-5" />, label: "Grande autonomie" },
        { icon: <Star className="w-5 h-5" />, label: "Confort premium" },
        { icon: <CheckCircle2 className="w-5 h-5" />, label: "Parfait pour VTC" }
      ],
      price: "400€",
      color: "Noir"
    },
    {
      name: "Ford Mustang Mach-E",
      type: "SUV 100% Électrique",
      image: "https://customer-assets.emergentagent.com/job_luxeo-tesla-vtc/artifacts/qo7d296n_ford-mustang-mach-e.jpg",
      features: [
        { icon: <Battery className="w-5 h-5" />, label: "100% Électrique" },
        { icon: <Gauge className="w-5 h-5" />, label: "440 km autonomie" },
        { icon: <Star className="w-5 h-5" />, label: "Design sportif" },
        { icon: <CheckCircle2 className="w-5 h-5" />, label: "Parfait pour VTC" }
      ],
      price: "350€",
      color: "Rouge"
    },
    {
      name: "Mercedes Classe V",
      type: "Van Premium",
      image: "https://customer-assets.emergentagent.com/job_luxeo-tesla-vtc/artifacts/ph7sgr9c_000000007502383.jpg",
      features: [
        { icon: <Users className="w-5 h-5" />, label: "Jusqu'à 7 places" },
        { icon: <Car className="w-5 h-5" />, label: "Espace généreux" },
        { icon: <Star className="w-5 h-5" />, label: "Luxe Mercedes" },
        { icon: <CheckCircle2 className="w-5 h-5" />, label: "Idéal groupes" }
      ],
      price: "800€",
      color: "Noir"
    }
  ];

  return (
    <div data-testid="home-page" className="overflow-hidden">
      <SEO 
        title="Luxeo Car | Location Tesla Model Y, Mustang Mach-E & Mercedes Classe V pour VTC Marseille"
        description="Louez une Tesla Model Y, Ford Mustang Mach-E ou Mercedes Classe V pour votre activité VTC à Marseille. Véhicules premium pour chauffeurs Uber et Bolt. À partir de 350€/semaine. Assurance incluse."
        keywords="location Tesla Model Y VTC, location véhicule VTC Marseille, Tesla chauffeur Uber, Mustang Mach-E location, Mercedes Classe V VTC, voiture électrique Uber Bolt, Luxeo Car"
        canonical="/"
      />
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center pt-20"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1678026039241-75a1becd25e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxUZXNsYSUyME1vZGVsJTIwWSUyMGJsYWNrJTIwZXh0ZXJpb3IlMjBzdHVkaW8lMjBsaWdodGluZyUyMGx1eHVyeXxlbnwwfHx8fDE3NzI5NzU2NjJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Tesla Model Y"
            className="w-full h-full object-cover object-center opacity-50 hero-image"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            {/* Badge */}
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-light mb-8 animate-fade-in"
            >
              <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse" />
              <span className="font-['Manrope'] text-sm text-[#A1A1AA]">
                Provence-Alpes-Côte d'Azur
              </span>
            </div>

            {/* Headline */}
            <h1 
              data-testid="hero-headline"
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up"
            >
              Chauffeurs VTC : Passez à l'électrique avec{' '}
              <span className="text-gradient-gold">Luxeo Car</span>
            </h1>

            {/* Subheadline */}
            <p 
              data-testid="hero-subheadline"
              className="font-['Manrope'] text-base lg:text-lg text-[#A1A1AA] max-w-2xl mx-auto mb-10 animate-fade-in-up delay-200"
            >
              Louez une Tesla Model Y, Ford Mustang Mach-E ou Mercedes Classe V idéale pour Uber et Bolt à partir de{' '}
              <span className="text-[#D4AF37] font-semibold">400€ / semaine</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="btn-primary text-base font-bold px-8 py-4 inline-flex items-center justify-center gap-2 group"
              >
                Faire une demande de location
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/location-vtc"
                data-testid="hero-cta-secondary"
                className="btn-secondary text-base font-medium px-8 py-4 inline-flex items-center justify-center gap-2"
              >
                Découvrir l'offre
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section 
        data-testid="why-section"
        className="section-padding bg-[#050505] relative"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-4">
              Pourquoi choisir <span className="text-[#D4AF37]">Luxeo Car</span> ?
            </h2>
            <p className="font-['Manrope'] text-[#A1A1AA] max-w-2xl mx-auto">
              Nous facilitons l'accès aux véhicules électriques premium pour les chauffeurs VTC professionnels.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                data-testid={`benefit-card-${index}`}
                className="feature-card card-luxeo p-8 group"
              >
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                  {benefit.icon}
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="font-['Manrope'] text-[#A1A1AA] text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicles Section */}
      <section 
        data-testid="vehicles-section"
        className="section-padding bg-[#0A0A0A] relative overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="font-['Outfit'] text-[#D4AF37] text-sm font-medium uppercase tracking-wider mb-4 block">
              Notre Flotte
            </span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-4">
              Véhicules <span className="text-[#D4AF37]">Disponibles</span>
            </h2>
            <p className="font-['Manrope'] text-[#A1A1AA] max-w-2xl mx-auto">
              Découvrez notre gamme de véhicules premium parfaitement adaptés à votre activité VTC.
            </p>
          </div>

          {/* Vehicles Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {vehicles.map((vehicle, index) => (
              <div
                key={index}
                data-testid={`vehicle-card-${index}`}
                className="card-luxeo overflow-hidden group"
              >
                {/* Vehicle Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={vehicle.image}
                    alt={vehicle.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 px-3 py-1 rounded-full glass-light">
                    <span className="font-['Manrope'] text-xs text-white">{vehicle.color}</span>
                  </div>
                </div>

                {/* Vehicle Info */}
                <div className="p-6">
                  <p className="font-['Manrope'] text-[#D4AF37] text-sm mb-2">{vehicle.type}</p>
                  <h3 className="font-['Outfit'] text-2xl font-bold text-white mb-4">
                    {vehicle.name}
                  </h3>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {vehicle.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-center gap-2"
                      >
                        <span className="text-[#D4AF37]">{feature.icon}</span>
                        <span className="font-['Manrope'] text-white/80 text-xs">
                          {feature.label}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Price & CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div>
                      <p className="font-['Manrope'] text-[#A1A1AA] text-xs">À partir de</p>
                      <p className="font-['Outfit'] text-2xl font-bold text-[#D4AF37]">
                        {vehicle.price}
                        {vehicle.price !== "Sur demande" && <span className="text-sm font-normal text-white/60">/sem</span>}
                      </p>
                    </div>
                    <Link
                      to="/contact"
                      data-testid={`vehicle-cta-${index}`}
                      className="btn-primary text-sm font-bold px-5 py-3"
                    >
                      Réserver
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section on Homepage */}
      <section 
        data-testid="home-contact-section"
        className="section-padding bg-[#050505] relative"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-['Outfit'] text-[#D4AF37] text-sm font-medium uppercase tracking-wider mb-4 block">
              Contact Rapide
            </span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-4">
              Demandez votre <span className="text-[#D4AF37]">véhicule</span>
            </h2>
            <p className="font-['Manrope'] text-[#A1A1AA]">
              Remplissez le formulaire et notre équipe vous contactera rapidement.
            </p>
          </div>

          {isSubmitted ? (
            <div data-testid="home-success-message" className="card-luxeo p-12 gold-glow text-center">
              <div className="w-20 h-20 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-8">
                <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
              </div>
              <h3 className="font-['Outfit'] text-3xl font-bold text-white mb-4">
                Demande envoyée !
              </h3>
              <p className="font-['Manrope'] text-[#A1A1AA] mb-8">
                Merci pour votre intérêt pour Luxeo Car. Notre équipe vous contactera dans les plus brefs délais.
              </p>
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({
                    full_name: '',
                    phone: '',
                    email: '',
                    is_vtc_driver: false,
                    platform: '',
                    rental_duration: '',
                    message: ''
                  });
                }}
                data-testid="home-new-request-btn"
                className="btn-secondary"
              >
                Nouvelle demande
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="home-lead-form"
              className="card-luxeo p-8 sm:p-10 gold-glow"
            >
              <div className="space-y-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="home_full_name" className="text-white">
                    Nom complet *
                  </Label>
                  <Input
                    id="home_full_name"
                    name="full_name"
                    type="text"
                    required
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="Jean Dupont"
                    data-testid="home-input-fullname"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>

                {/* Phone & Email Row */}
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="home_phone" className="text-white">
                      Téléphone *
                    </Label>
                    <Input
                      id="home_phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+33 6 00 00 00 00"
                      data-testid="home-input-phone"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="home_email" className="text-white">
                      Email *
                    </Label>
                    <Input
                      id="home_email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="jean@example.com"
                      data-testid="home-input-email"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                    />
                  </div>
                </div>

                {/* VTC Driver Checkbox */}
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="home_is_vtc_driver"
                    checked={formData.is_vtc_driver}
                    onCheckedChange={handleCheckboxChange}
                    data-testid="home-checkbox-vtc"
                    className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                  />
                  <Label htmlFor="home_is_vtc_driver" className="text-white cursor-pointer">
                    Je suis chauffeur VTC
                  </Label>
                </div>

                {/* Platform & Duration Row */}
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-white">
                      Plateforme utilisée
                    </Label>
                    <Select
                      value={formData.platform}
                      onValueChange={(value) => handleSelectChange('platform', value)}
                    >
                      <SelectTrigger
                        data-testid="home-select-platform"
                        className="bg-white/5 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                      >
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        <SelectItem value="uber" className="text-white focus:bg-white/10">Uber</SelectItem>
                        <SelectItem value="bolt" className="text-white focus:bg-white/10">Bolt</SelectItem>
                        <SelectItem value="both" className="text-white focus:bg-white/10">Les deux</SelectItem>
                        <SelectItem value="other" className="text-white focus:bg-white/10">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">
                      Véhicule souhaité
                    </Label>
                    <Select
                      value={formData.rental_duration}
                      onValueChange={(value) => handleSelectChange('rental_duration', value)}
                    >
                      <SelectTrigger
                        data-testid="home-select-vehicle"
                        className="bg-white/5 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                      >
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        <SelectItem value="tesla-model-y" className="text-white focus:bg-white/10">Tesla Model Y</SelectItem>
                        <SelectItem value="mustang-mach-e" className="text-white focus:bg-white/10">Ford Mustang Mach-E</SelectItem>
                        <SelectItem value="mercedes-classe-v" className="text-white focus:bg-white/10">Mercedes Classe V</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="home_message" className="text-white">
                    Message (optionnel)
                  </Label>
                  <Textarea
                    id="home_message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Précisez votre demande ou posez vos questions..."
                    rows={3}
                    data-testid="home-input-message"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="home-submit-btn"
                  className="btn-primary w-full text-base font-bold py-4 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Demander un véhicule
                    </>
                  )}
                </button>

                <p className="font-['Manrope'] text-[#52525B] text-xs text-center">
                  En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe.
                  Vous pouvez aussi nous contacter directement à{' '}
                  <a href="mailto:luxeocar@gmail.com" className="text-[#D4AF37] hover:underline">
                    luxeocar@gmail.com
                  </a>
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section 
        data-testid="cta-section"
        className="section-padding bg-[#0A0A0A] relative"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="card-luxeo p-10 sm:p-16 gold-glow">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-6">
              Prêt à démarrer votre activité VTC ?
            </h2>
            <p className="font-['Manrope'] text-[#A1A1AA] mb-10 max-w-xl mx-auto">
              Rejoignez les chauffeurs qui ont fait le choix de l'électrique avec Luxeo Car.
              Faites votre demande dès aujourd'hui.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="final-cta"
                className="btn-primary text-lg font-bold px-10 py-5 inline-flex items-center gap-2 group"
              >
                Faire une demande de location
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="tel:0620202996"
                data-testid="final-cta-phone"
                className="btn-secondary text-lg font-medium px-10 py-5 inline-flex items-center gap-2"
              >
                Appeler le 06 20 20 29 96
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
